import React from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "consts";
import { PageMain } from "components/molecules";
import { PageRichHeader } from "components/molecules/PageHeader/PageRichHeader";
import { PaziaButton, PaziaLoader } from "components/atoms";
import { TanTableClient, TanTableColumn } from "components/molecules";
import { CourseGroupImageSizeApi, useCourseGroupImageSizes } from "api/coursegroups";

const WARN_THRESHOLD_BYTES = 500 * 1024;
const DANGER_THRESHOLD_BYTES = 1024 * 1024;

type StatusKind = "ok" | "warn" | "danger" | "missing" | "missing-file";

const resolveStatus = (row: CourseGroupImageSizeApi): StatusKind => {
  if (row.missing) return "missing-file";
  if (row.mainImage == null || row.sizeBytes == null) return "missing";
  if (row.sizeBytes > DANGER_THRESHOLD_BYTES) return "danger";
  if (row.sizeBytes > WARN_THRESHOLD_BYTES) return "warn";
  return "ok";
};

const statusLabel: Record<StatusKind, string> = {
  ok: "OK",
  warn: "Uwaga",
  danger: "Za duży",
  missing: "Brak obrazka",
  "missing-file": "Nieznaleziony plik",
};

const statusClasses: Record<StatusKind, string> = {
  ok: "bg-emerald-100 text-emerald-700 dark:bg-emerald-700/30 dark:text-emerald-300",
  warn: "bg-amber-100 text-amber-700 dark:bg-amber-700/30 dark:text-amber-300",
  danger: "bg-red-100 text-red-700 dark:bg-red-700/30 dark:text-red-300",
  missing: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
  "missing-file": "bg-red-100 text-red-700 dark:bg-red-700/30 dark:text-red-300",
};

const formatBytes = (bytes: number | null): string => {
  if (bytes == null) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export const CourseGroupImagesPage = () => {
  const navigate = useNavigate();
  const { isLoading, imageSizes } = useCourseGroupImageSizes();

  const sortedData = React.useMemo(() => {
    return [...imageSizes].sort((a, b) => {
      const aSize = a.sizeBytes ?? -1;
      const bSize = b.sizeBytes ?? -1;
      return bSize - aSize;
    });
  }, [imageSizes]);

  const totalBytes = React.useMemo(
    () => imageSizes.reduce((sum, r) => sum + (r.sizeBytes ?? 0), 0),
    [imageSizes],
  );
  const withImageCount = React.useMemo(
    () => imageSizes.filter((r) => r.sizeBytes != null).length,
    [imageSizes],
  );
  const averageBytes = withImageCount > 0 ? totalBytes / withImageCount : 0;
  const missingCount = imageSizes.filter((r) => r.mainImage == null).length;
  const overWarnCount = imageSizes.filter(
    (r) => (r.sizeBytes ?? 0) > WARN_THRESHOLD_BYTES,
  ).length;
  const overDangerCount = imageSizes.filter(
    (r) => (r.sizeBytes ?? 0) > DANGER_THRESHOLD_BYTES,
  ).length;
  const missingFileCount = imageSizes.filter((r) => r.missing).length;

  const columns = React.useMemo<TanTableColumn<CourseGroupImageSizeApi>[]>(
    () => [
      {
        id: "thumbnail",
        header: "",
        size: 64,
        enableSorting: false,
        enableColumnFilter: false,
        cell: (info) => {
          const row = info.row.original;
          if (!row.mainImage) {
            return (
              <div className="h-10 w-10 rounded bg-slate-100 dark:bg-slate-700" />
            );
          }
          return (
            <img
              src={row.mainImage}
              alt={row.name}
              className="h-10 w-10 rounded object-cover"
              loading="lazy"
            />
          );
        },
      },
      {
        id: "name",
        accessorKey: "name",
        header: "Nazwa grupy",
        enableColumnFilter: false,
      },
      {
        id: "sizeBytes",
        accessorKey: "sizeBytes",
        header: "Rozmiar",
        size: 140,
        enableColumnFilter: false,
        sortingFn: (a, b) => {
          const av = a.original.sizeBytes ?? -1;
          const bv = b.original.sizeBytes ?? -1;
          return av - bv;
        },
        cell: (info) => formatBytes(info.row.original.sizeBytes),
      },
      {
        id: "status",
        header: "Status",
        size: 180,
        enableSorting: false,
        enableColumnFilter: false,
        cell: (info) => {
          const status = resolveStatus(info.row.original);
          return (
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${statusClasses[status]}`}
            >
              {statusLabel[status]}
            </span>
          );
        },
      },
    ],
    [],
  );

  return (
    <PageMain>
      <PageRichHeader header="Rozmiary obrazków grup kursów">
        <div className="flex grow justify-end">
          <PaziaButton
            type="secondary"
            title="Grupy kursów"
            onClick={() => navigate(routes.COURSE_GROUPS)}
          />
        </div>
      </PageRichHeader>

      {isLoading ? (
        <PaziaLoader position="center" />
      ) : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <SummaryTile label="Łączny rozmiar" value={formatBytes(totalBytes)} />
            <SummaryTile label="Średnia" value={formatBytes(Math.round(averageBytes))} />
            <SummaryTile label="Grup" value={imageSizes.length.toString()} />
            <SummaryTile label="Bez obrazka" value={missingCount.toString()} />
            <SummaryTile
              label={`> ${formatBytes(WARN_THRESHOLD_BYTES)}`}
              value={overWarnCount.toString()}
              tone={overWarnCount > 0 ? "amber" : "default"}
            />
            <SummaryTile
              label={`> ${formatBytes(DANGER_THRESHOLD_BYTES)}`}
              value={overDangerCount.toString()}
              tone={overDangerCount > 0 ? "red" : "default"}
            />
          </div>

          {missingFileCount > 0 && (
            <div className="mb-4 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-700 dark:bg-red-900/20 dark:text-red-300">
              {missingFileCount} grup{missingFileCount === 1 ? "a ma" : " ma"} obrazek zapisany w bazie, ale plik nie został znaleziony w Azure Blob Storage.
            </div>
          )}

          <TanTableClient
            data={sortedData}
            columns={columns}
            pageSize={50}
            onRowClick={(r: CourseGroupImageSizeApi) =>
              navigate(routes.COURSE_GROUPS_EDIT(r.id.toString()))
            }
          />
        </>
      )}
    </PageMain>
  );
};

interface SummaryTileProps {
  label: string;
  value: string;
  tone?: "default" | "amber" | "red";
}

const toneClasses: Record<NonNullable<SummaryTileProps["tone"]>, string> = {
  default: "bg-white dark:bg-slate-800",
  amber: "bg-amber-50 dark:bg-amber-900/20",
  red: "bg-red-50 dark:bg-red-900/20",
};

const SummaryTile = ({ label, value, tone = "default" }: SummaryTileProps) => (
  <div
    className={`rounded border border-slate-200 px-3 py-2 dark:border-slate-700 ${toneClasses[tone]}`}
  >
    <div className="text-xs text-slate-500 dark:text-slate-400">{label}</div>
    <div className="text-lg font-semibold">{value}</div>
  </div>
);
